// Кой ред на „Продадени продукти/рецепти" е отворен в панела вдясно и как се
// влиза/излиза — огледало на `stockCard/useStockCardSelection` за редове по
// `sellableId`: клик или Enter на ред го отваря, ↑/↓ в тесния списък сменят
// реда (`select`), Esc затваря — с preventDefault, иначе същото натискане
// минава и през глобалната Esc навигация (SharedAccountGate). При отворен antd
// слой (модалът на документ, падащо меню, календар, подсказка) Esc е негов.
// След затваряне фокусът се връща на реда в таблицата, за да продължи ↑/↓.
// `scopeKey` (продукти ↔ рецепти) — смяната му затваря панела без връщане на
// фокус: страницата е една и съща инстанция за двете справки.
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { hasOpenOverlay } from "../../../../lib/overlay";
import type { SoldSellable } from "../../../../api/inventory";

type Options<T extends SoldSellable> = {
  scopeKey: string;
  // всички редове на периода — след презареждане панелът чете свежия ред
  allRows: T[];
  // редовете на таблицата в реда на екрана — за връщането на фокуса
  visibleRows: T[];
  // контейнерът на таблицата; редовете ѝ са `[data-row][data-sellable-id]`
  tableRef: RefObject<HTMLElement | null>;
};

const tableRowOf = (container: HTMLElement | null, sellableId: number, index: number) =>
  container?.querySelector<HTMLElement>(`[data-row][data-sellable-id="${sellableId}"]`) ??
  container?.querySelectorAll<HTMLElement>("[data-row]")[index] ??
  null;

// Избраният таб в панела — Enter/→ от тесния списък влиза в него
export const focusSoldPanel = () =>
  document.querySelector<HTMLElement>('[data-sold-panel] [role="tab"][aria-selected="true"]')?.focus();

export function useSoldSelection<T extends SoldSellable>({ scopeKey, allRows, visibleRows, tableRef }: Options<T>) {
  const [opened, setOpened] = useState<{ scopeKey: string; row: T } | null>(null);
  const restoreFocusIdRef = useRef<number | null>(null);

  const openedRow = opened && opened.scopeKey === scopeKey ? opened.row : null;
  const selected = openedRow
    ? (allRows.find((row) => row.sellableId === openedRow.sellableId) ?? openedRow)
    : null;

  const open = useCallback((row: T) => setOpened({ scopeKey, row }), [scopeKey]);

  const close = useCallback(() => {
    restoreFocusIdRef.current = openedRow?.sellableId ?? null;
    setOpened(null);
  }, [openedRow]);

  // Esc затваря панела, докато е отворен
  useEffect(() => {
    if (!openedRow) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || hasOpenOverlay()) return;
      event.preventDefault();
      close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openedRow, close]);

  // Фокусът обратно на реда (таблицата се връща чак след рендера)
  useEffect(() => {
    const sellableId = restoreFocusIdRef.current;
    if (openedRow || sellableId == null) return;
    const index = visibleRows.findIndex((row) => row.sellableId === sellableId);
    // Ref-ът се чисти чак в кадъра: пререндер преди него отменя кадъра и
    // ефектът го пуска наново
    const frame = requestAnimationFrame(() => {
      restoreFocusIdRef.current = null;
      tableRowOf(tableRef.current, sellableId, index)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [openedRow, visibleRows, tableRef]);

  return { selected, open, select: open, close };
}
